import type { IconType } from "react-icons";
import { IoBriefcase, IoChatbubble, IoCreate, IoCube, IoDocumentText, IoGrid, IoMail, IoPerson } from "react-icons/io5";

/** Home-page section ids, as rendered by `BaseSection` in `HomePage.tsx`. */
export type DeviceSectionId = "profile" | "services" | "portfolio" | "apps" | "resume" | "blog" | "faq" | "contact";

export type DeviceSection = {
    id: DeviceSectionId;
    /** Short icon label under `deviceOs.apps`. */
    labelKey: string;
    icon: IconType;
    /** Liquid Glass app icon (256px PNG) drawn in the app tiles. */
    image: string;
};

/**
 * The site's sections as device apps, in page order. The phone's home screen
 * scrolls to them; the tablet and desktop gate opens each one as a window.
 */
export const DEVICE_SECTIONS: ReadonlyArray<DeviceSection> = [
    { id: "profile", labelKey: "deviceOs.apps.about", icon: IoPerson, image: "/apps/os-icons/about.png" },
    { id: "services", labelKey: "deviceOs.apps.services", icon: IoBriefcase, image: "/apps/os-icons/services.png" },
    { id: "portfolio", labelKey: "deviceOs.apps.work", icon: IoGrid, image: "/apps/os-icons/work.png" },
    { id: "apps", labelKey: "deviceOs.apps.apps", icon: IoCube, image: "/apps/os-icons/apps.png" },
    { id: "resume", labelKey: "deviceOs.apps.resume", icon: IoDocumentText, image: "/apps/os-icons/resume.png" },
    { id: "blog", labelKey: "deviceOs.apps.blog", icon: IoCreate, image: "/apps/os-icons/blog.png" },
    { id: "faq", labelKey: "deviceOs.apps.faq", icon: IoChatbubble, image: "/apps/os-icons/faq.png" },
    { id: "contact", labelKey: "deviceOs.apps.contact", icon: IoMail, image: "/apps/os-icons/contact.png" },
];

/** The Liquid Glass icon for one section, for tiles outside the section grids (lock-screen notes). */
export function getSectionImage(id: DeviceSectionId): string {
    return DEVICE_SECTIONS.find((section) => section.id === id)?.image ?? "";
}

export function isDeviceSectionId(value: string): value is DeviceSectionId {
    return DEVICE_SECTIONS.some((section) => section.id === value);
}

/**
 * Status-bar clock, iOS style: "9:41" in English (no AM/PM, which the real
 * status bar drops too), 24-hour in Chinese.
 */
export function formatDeviceClock(date: Date, language: string): string {
    if (language.startsWith("zh")) {
        return date.toLocaleTimeString(language, { hour: "2-digit", minute: "2-digit", hour12: false });
    }
    const hours = date.getHours() % 12 || 12;
    return `${hours}:${String(date.getMinutes()).padStart(2, "0")}`;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** How many posts went out in the 30 days before `now`: the blog app's badge. */
export function countRecentPosts(dates: ReadonlyArray<string>, now: Date, windowDays = 30): number {
    const since = now.getTime() - windowDays * DAY_MS;
    return dates.filter((date) => {
        const time = new Date(date).getTime();
        return !Number.isNaN(time) && time > since && time <= now.getTime();
    }).length;
}
